import { create } from 'zustand';
import { serviceCategoriesApi } from '../../../api/api';
import type { ServiceCategoryResponse, CreateServiceCategoryRequest, UpdateServiceCategoryRequest } from '../../../api/generated';

type ServiceCategoryState = {
  categories: ServiceCategoryResponse[];
  loading: boolean;
  error: string | null;

  // Actions
  loadCategories: () => Promise<void>;
  createCategory: (categoryData: CreateServiceCategoryRequest) => Promise<ServiceCategoryResponse>;
  updateCategory: (id: string, categoryData: UpdateServiceCategoryRequest) => Promise<ServiceCategoryResponse>;
  deleteCategory: (id: string) => Promise<void>;

  clearError: () => void;
};

export const useServiceCategoryStore = create<ServiceCategoryState>(set => ({
  categories: [],
  loading: false,
  error: null,

  loadCategories: async () => {
    set({ loading: true, error: null });

    try {
      const response = await serviceCategoriesApi.getAllCategories();

      // API nyní vrací přímo pole kategorií
      const categories = response.data;
      set({ categories, loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při načítání kategorií',
        loading: false,
      });
    }
  },

  createCategory: async categoryData => {
    set({ loading: true, error: null });

    try {
      const response = await serviceCategoriesApi.createCategory({
        createServiceCategoryRequest: categoryData,
      });

      const newCategory = response.data;
      set(state => ({
        categories: [...state.categories, newCategory],
        loading: false,
      }));

      return newCategory;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při vytváření kategorie',
        loading: false,
      });
      throw error;
    }
  },

  updateCategory: async (id, categoryData) => {
    set({ loading: true, error: null });

    try {
      const response = await serviceCategoriesApi.updateCategory({
        id,
        updateServiceCategoryRequest: categoryData,
      });

      const updatedCategory = response.data;
      set(state => ({
        categories: state.categories.map(c => (c.id === id ? updatedCategory : c)),
        loading: false,
      }));

      return updatedCategory;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při úpravě kategorie',
        loading: false,
      });
      throw error;
    }
  },

  deleteCategory: async id => {
    set({ loading: true, error: null });

    try {
      await serviceCategoriesApi.deleteCategory({ id });

      set(state => ({
        categories: state.categories.filter(c => c.id !== id),
        loading: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při mazání kategorie',
        loading: false,
      });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
